<template>
  <div class="login-form">
    <div class="title">
      {{ displayTitle }}
    </div>

    <div class="tip" v-if="!$isPure">
      <span v-if="activeKey === 'login'">
        {{ $t('mei-you-zhang-hao') }}
        <a class="cursor-pointer" @click="onToggleKey">
          {{ $t('li-ji-zhu-ce') }}
        </a>
      </span>
      <span v-if="activeKey === 'register'">
        {{ $t('yi-you-zhang-hao') }}
        <a class="cursor-pointer" @click="onToggleKey">
          {{ $t('li-ji-deng-lu') }}
        </a>
      </span>
    </div>
    <a-form
      :model="formState"
      name="basic"
      :label-col="{ span: 0 }"
      :wrapper-col="{ span: 24 }"
      autocomplete="off"
      class="sea-form"
      @finish="onFinish"
      @finishFailed="onFinishFailed"
    >
      <template v-if="activeKey === 'register'">
        <a-form-item name="email">
          <a-input
            v-model:value="formState.email"
            class="login-input"
            :placeholder="$t('qing-shu-ru-you-xiang')"
          />
        </a-form-item>
        <a-form-item name="sms_code">
          <XCaptchaInput
            v-model:value="formState.sms_code"
            :placeholder="$t('qing-shu-ru-yan-zheng-ma')"
            :xLarge="true"
            :data="formState.email"
            :is-checked="formState.remember"
            type="link"
          />
        </a-form-item>
        <a-form-item name="password" auto-complete="off">
          <a-input-password
            v-model:value="formState.password"
            class="login-input"
            size="large"
            autoComplete="new-password"
            :placeholder="$t('xin-mi-ma')"
          />
          <div
            :class="{
              'pwd-rule-tip': true,
            }"
          >
            <div class="pwd-rule-icon">
              <iconpark-icon
                :name="
                  PwdValidator.isSafeRange(formState.password) ? 'circle-success' : 'close-fill'
                "
              />
            </div>
            <div class="pwd-rule-text">
              {{ $t('chang-du-wei-620-ge-zi-fu') }}
            </div>
          </div>
          <div
            :class="{
              'pwd-rule-tip': true,
            }"
          >
            <div class="pwd-rule-icon">
              <iconpark-icon
                :name="
                  PwdValidator.isValidFormat(formState.password) ? 'circle-success' : 'close-fill'
                "
              />
            </div>
            <div class="pwd-rule-text">
              {{ $t('bao-han-shu-zi-da-xie-zi-mu-xiao-xie-zi-fu-zi-fu-san-zhong-ji-yi-shang') }}
            </div>
          </div>
        </a-form-item>
        <a-form-item name="checkPassword" autoComplete="off">
          <a-input-password
            v-model:value="formState.checkPassword"
            size="large"
            class="login-input"
            :placeholder="$t('qing-que-ren-mi-ma')"
            autoComplete="new-password"
          />
        </a-form-item>
      </template>
      <div v-if="activeKey === 'login'" class="relative">
        <a-form-item name="email">
          <a-input
            v-model:value="formState.email"
            class="login-input"
            :placeholder="$t('qing-shu-ru-you-xiang')"
          />
        </a-form-item>
        <a-form-item name="password">
          <a-input-password
            v-model:value="formState.password"
            class="login-input"
            :placeholder="$t('qing-shu-ru-mi-ma')"
          />
        </a-form-item>
      </div>

      <a-button
        type="primary"
        :disabled="!canSubmit"
        html-type="submit"
        :loading="loading"
        class="login-btn"
      >
        {{ activeKey === 'login' ? $t('deng-lu') : $t('zhu-ce') }}
      </a-button>

      <a v-if="activeKey === 'login' && !$isPure" class="forget-password" @click="onOpenReset">
        {{ $t('wang-ji-mi-ma') }}
      </a>

      <template v-if="!$isPure">
        <a-divider class="or">{{ $t('or-continue-with') }}</a-divider>

        <GoogleLogin
          class="google-login-btn"
          :disabled="!formState.remember"
          @success="handleGoogleLoginSuccess"
        />

        <div name="remember" class="confirm-protocol">
          {{ $t('by-signing-up-to-the-platform-you-understand-and-agree-to-our') }}
          <a
            class="link"
            :href="'https://protocol.rendora.ai/legal/user'"
            name="user"
            target="_blank"
          >
            {{ $t('mo-fa-you-yan-yong-hu-xie-yi') }}
          </a>
          {{ $t('huo') }}
          <a
            class="link"
            :href="'https://protocol.rendora.ai/legal/privacy-policy'"
            name="policy"
            target="_blank"
          >
            {{ $t('mo-fa-you-yan-yin-si-zheng-ce') }}
          </a>
        </div>
      </template>
    </a-form>
  </div>
</template>
<script lang="ts" setup>
import { getUserInfo, loginByEmail, registerByEmail } from '@/api';
import { message } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
import type { LoginInfo } from '~/api/interface';
import { withRegisterOrLoginChannelForUs } from '~/api/module/open-login';
import { getLocation } from '~/api/module/seo';
import { ServerErrorCode } from '~/api/request';
import { BROADCAST_KEY, SESSION_SOURCE } from '~/constants/config';
import { useTime } from '~/hooks/useTime';
import { concurrency } from '~/utils/concurrency';
import { PwdValidator, transformPublicKey } from '~/utils/form';
import { sessionRemoveItem } from '~/utils/localStorage';
import { emitBroadcast, TAB_ID } from '~/utils/postMessage';
import { emailReg } from '~/utils/regExp';
import { buriedLogin, LOGIN_WAY, TRACK_EVENT, trackEvent, USER_TYPE } from '~/utils/track';
import GoogleLogin from './googleLogin.vue';
import { trackData } from './trackData';
import { useProtocolConfirm } from './useProtocolConfirm';

const { show: showProtocolConfirm } = useProtocolConfirm();
const { t: $t } = useI18n();
const { $isPure } = useNuxtApp();

interface FormState {
  email: string;
  // email: string
  checkPassword: string;
  sms_code: string;
  password: string;
  remember: boolean;
}

const formState = reactive<FormState>({
  email: '',
  sms_code: '',
  password: '',
  checkPassword: '',
  remember: true,
});
const { setAccountList, setSession, setUserInfo } = useChangeUser();
const loading = ref(false);
const emits = defineEmits(['back', 'success', 'accountSelect']);

type LoginType = 'register' | 'login';

const activeKey = ref<LoginType>('login');

const displayTitle = computed(() => {
  if ($isPure) {
    return '3D Virtual Human Platform';
  }
  return activeKey.value === 'login'
    ? $t('huan-ying-deng-lu-you-yan-fu-wu-ping-tai')
    : $t('chuang-jian-yi-ge-zhang-hu');
});

const onToggleKey = () => {
  trackEvent(TRACK_EVENT.YOUYAN_LOGIN_CLICK_REGISTER);
  activeKey.value = activeKey.value !== 'login' ? 'login' : 'register';
};

const canSubmit = computed(() => {
  if (activeKey.value === 'register') {
    return formState.sms_code && formState.email;
  }

  return formState.password && formState.email;
});

const confirm = async () => {
  formState.remember = true;
  await onFinish(toRaw(formState));
};

// 打开忘记密码
const onOpenReset = () => {
  trackEvent(TRACK_EVENT.CLICK_LOGIN_FORGET_BTN);
  emits('back');
};

const { $dayjs } = useTime();

const handleGoogleLoginSuccess = async (data: LoginInfo[]) => {
  const newUser = $dayjs().isSame($dayjs(data[0]?.create_time), 'day');

  trackData(
    'google',
    { email: data[0]?.email_address },
    {
      NewUser: newUser ? USER_TYPE.NEW : USER_TYPE.OLD,
      LoginWay: LOGIN_WAY.GOOGLE,
    },
  );

  if (data.length > 1) {
    // 用户账号列表
    setAccountList(data);
    emits('accountSelect', data);
  } else {
    // 用户token
    await setSession(data[0]);
    emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });
    buriedLogin(data[0]?.user_id);
    const result = await getUserInfo();

    // 用户信息
    await setUserInfo(result.data as any);

    emits('success', result.data);
  }
};

const onFinish = async (values: any) => {
  // 手动校验表单
  const validationErrors: string[] = [];

  if (activeKey.value === 'register') {
    // 校验邮箱
    if (!values.email) {
      validationErrors.push($t('qing-shu-ru-you-xiang'));
    } else if (!emailReg.test(values.email)) {
      validationErrors.push($t('you-xiang-ge-shi-bu-zheng-que'));
    }

    // 校验验证码
    if (!values.sms_code) {
      validationErrors.push($t('qing-shu-ru-yan-zheng-ma'));
    }

    // 校验密码
    if (!values.password) {
      validationErrors.push($t('qing-shu-ru-mi-ma'));
    } else if (!PwdValidator.isSafeRange(values.password)) {
      validationErrors.push($t('mi-ma-chang-du-bu-fu-he-yao-qiu'));
    } else if (!PwdValidator.isValidFormat(values.password)) {
      validationErrors.push($t('mi-ma-ge-shi-bu-fu-he-yao-qiu'));
    }

    // 校验确认密码
    if (!values.checkPassword) {
      validationErrors.push($t('qing-que-ren-mi-ma'));
    } else if (values.checkPassword !== values.password) {
      validationErrors.push($t('liang-ci-mi-ma-shu-ru-bu-yi-zhi-qing-zhong-xin-shu-ru'));
    }
  } else {
    // 校验邮箱
    if (!values.email) {
      validationErrors.push($t('qing-shu-ru-you-xiang'));
    } else if (!emailReg.test(values.email)) {
      validationErrors.push($t('you-xiang-ge-shi-bu-zheng-que'));
    }

    // 校验密码
    if (!values.password) {
      validationErrors.push($t('qing-shu-ru-mi-ma'));
    }
  }

  // 如果有校验错误，显示第一个错误信息并返回
  if (validationErrors.length > 0) {
    message.error(validationErrors[0]);
    return;
  }

  if (!formState.remember) {
    // message.warning($t('qing-xian-yue-du-bing-gou-xuan-tiao-kuan'))
    showProtocolConfirm(confirm);
    return;
  }
  loading.value = true;

  try {
    let res;

    let newUser = false;

    if (activeKey.value === 'register') {
      newUser = true;
      const [res1, res2] = await concurrency([
        transformPublicKey({
          email: values.email,
          password: values.password,
          verify_code: values.sms_code,
        }),
        getLocation(),
      ]);

      const [encrypted, rsa] = res1!;

      res = await registerByEmail(
        withRegisterOrLoginChannelForUs({
          rsa_key_id: rsa?.id,
          auth_data: encrypted,
          country: res2.country,
        }),
      );
    } else {
      newUser = false;
      const [res1, res2] = await concurrency([
        transformPublicKey({
          email: values.email,
          password: values.password,
        }),
        getLocation(),
      ]);

      const [encrypted, rsa] = res1;

      res = await loginByEmail(
        withRegisterOrLoginChannelForUs({
          rsa_key_id: rsa?.id,
          auth_data: encrypted,
          country: res2.country,
        }),
        {
          autoErrorHandle: false,
        },
      );

      if (
        [ServerErrorCode.USER_EXIST_ERROR, ServerErrorCode.EMAIL_NOT_REGISTERED].includes(res.code)
      ) {
        activeKey.value = 'register';
        throw new Error(res.msg);
      }

      if (res.code !== 0) {
        message.error(res.msg);
        throw new Error(res.msg);
      }
    }

    if (res.data) {
      const { data } = res;

      // 用户账号列表
      setAccountList(data);

      // 优先使用后端返回的 is_new_user 字段，如果没有则使用前端判断
      const isNewUser = data[0]?.is_new_user !== undefined ? data[0].is_new_user : newUser;

      trackData(
        'google',
        { email: values.email },
        {
          LoginWay: LOGIN_WAY.EMAIL,
          NewUser: isNewUser ? USER_TYPE.NEW : USER_TYPE.OLD,
          // InviterId: res.data[0]?.invitation_info?.user_id || undefined,
        },
      );

      // 埋点：邮箱登录成功
      trackEvent(TRACK_EVENT.LOGIN_EMAIL_SUCCESS, {
        account_name: values.email,
        NewUser: isNewUser ? USER_TYPE.NEW : USER_TYPE.OLD,
        login_type: activeKey.value,
      });

      if (data.length > 1) {
        emits('accountSelect', data);
      } else {
        // 用户token
        await setSession(res.data[0]);

        emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });
        buriedLogin(res.data[0]?.user_id);
        const result = await getUserInfo();

        // 用户信息
        setUserInfo(result.data as any);

        emits('success', result.data);
      }
      sessionRemoveItem(SESSION_SOURCE);

      loading.value = false;
    }
  } catch (error: any) {
    console.log(error);
    loading.value = false;
    // 埋点：邮箱登录失败
    const errorCode = error?.code || error?.error_code || 'unknown';
    const errorMsg = error?.msg || error?.error_reason || error?.message || '未知错误';

    // 根据错误类型记录不同的失败埋点
    if (errorCode === 10003) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_PASSWORD_ERROR, {
        login_type: 'email',
        remaining_num: error?.data?.remaining_num,
      });
    } else if (errorCode === 10002) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_LOCKED, {
        login_type: 'email',
        expired_time: error?.data?.expired_time,
      });
    } else if (
      [ServerErrorCode.USER_EXIST_ERROR, ServerErrorCode.EMAIL_NOT_REGISTERED].includes(errorCode)
    ) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_NOT_EXIST, {
        login_type: 'email',
        error_code: errorCode,
      });
    } else if (errorCode === 'network_error' || error?.message?.includes('network')) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_NETWORK_ERROR, {
        login_type: 'email',
      });
    } else {
      trackEvent(TRACK_EVENT.LOGIN_EMAIL_FAIL, {
        login_type: activeKey.value,
        error_code: errorCode,
        error_msg: errorMsg,
      });
    }
  }
};

const onFinishFailed = (errorInfo: any) => {
  // console.log('Failed:', errorInfo)
};
</script>

<style lang="scss" scoped>
.login-form {
  width: 328px;

  .title {
    text-align: center;
    font-size: 24px;
    font-weight: 500;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tip {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    height: 17px;
    text-align: center;
    font-size: 12px;
    color: var(--text-1-80);
    a {
      font-size: 12px;
      font-weight: 400;
    }
  }

  .login-input {
    height: 44px;
    border-radius: 8px;
    letter-spacing: 0.3px;
    padding: 0 16px;
    box-sizing: border-box;
    &:deep(.ant-input:placeholder-shown) {
      font-size: 14px;
      letter-spacing: 0.3px;
      color: var(--text-1-40);
    }
  }

  .sea-form {
    margin-top: 28px;
    .ant-form-item {
      margin-bottom: 16px;
    }
  }

  .login-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 44px;
    border-radius: 8px;
    margin-top: 36px;
    letter-spacing: 0.3px;
  }

  .forget-password {
    margin-top: 24px;
    font-size: 13px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-1);
    &:hover {
      color: var(--purple-6);
    }
  }

  .or {
    margin-top: 22px;
    margin-bottom: 8px;
    &:deep(.ant-divider-inner-text) {
      font-size: 14px;
      font-weight: 600;
      color: #000;
    }
  }

  .google-login-btn {
    height: 44px;
    width: 100%;
  }

  .confirm-protocol {
    font-size: 12px !important;
    color: var(--text-1-80);
    line-height: 20px;
    margin-top: 52px;
    .link {
      font-size: 12px !important;
      color: var(--purple-6);
      font-weight: 500;
    }
  }

  .pwd-rule-tip {
    display: flex;
    margin-top: 14px;
    font-size: 12px;
    gap: 4px;
    justify-content: start;
    align-items: flex-start;
    color: var(--text-1-80);
    line-height: 16px;

    .pwd-rule-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
    }
    .pwd-rule-text {
      flex: 1;
    }
  }
}
</style>
